import { CardTitleLookup } from '@/constants/ReportDashboard/cards-metadata'
import { useFactorLens, useIsAnalysisLoading } from '@/contexts/RADashboardDataContext/hooks'
import { useTheme } from '@/contexts/RADashboardThemeContext/hooks'
import { FactorLensFocus, FactorLensState } from '@/interfaces/factorLens.interface'
import { CardProps, cardPrefsContextKey } from '@/interfaces/raDashboard.interface'
import { FactorLensDataSet } from '@/services/report-dashboard/raDashboard.data.service'
import { putUserPreference } from '@/services/userPreference.service'
import { buttonGroupDeselectedStyle, buttonGroupSelectedStyle, cardStyle, cardSubtitleStyle, cardTitleStyle } from '@/styles/reportDashboardStyles'
import { Button, CircularProgress } from '@mui/material'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import React, { useEffect } from 'react'
import FactorContributions from './FactorContributions'
import FactorLensGrid from './FactorLensGrid'
import FactorRiskTable from './FactorRiskTable'
import FactorWaterfall from './FactorWaterfall'
import { useFactorRiskColors } from './factorRiskColors'

const DATA_TYPE_OPTIONS = ['Portfolio', 'Benchmark', 'Active']

const FactorRiskCard: React.FC<CardProps> = (props) => {
  const theme = useTheme();
  const factorRiskColors = useFactorRiskColors()

  const [cardState, setCardState] = React.useState<FactorLensState>({
    selectedDataType: 'Portfolio',
    selectedFactorBox: null,
    contributionsShown: true,
    waterfallShown: true,
  });

  // Track which dashboard we have already hydrated card-state for so saves don't re-apply.
  // Starts null so the first time cardPrefs arrives we run the hydration effect once.
  const initializedForDashboard = React.useRef<string | null>(null)

  useEffect(() => {
    const dashboardName = props.cardPrefs?.dashboardName
    if (dashboardName && dashboardName !== initializedForDashboard.current) {
      const fl = props.cardPrefs?.factorLens
      initializedForDashboard.current = dashboardName
      setCardState({
        selectedDataType: fl?.selectedDataType && DATA_TYPE_OPTIONS.includes(fl.selectedDataType) ? fl.selectedDataType : 'Portfolio',
        selectedFactorBox: null,
        contributionsShown: fl?.contributionsShown ?? true,
        waterfallShown: fl?.waterfallShown ?? true,
      })
    }
  }, [props.cardPrefs])

  const saveCardPrefs = (next: { selectedDataType: string; contributionsShown: boolean; waterfallShown: boolean }) => {
    if (!props.cardPrefs?.dashboardName) return
    const updated = {
      ...props.cardPrefs,
      factorLens: {
        ...props.cardPrefs.factorLens,
        selectedDataType: next.selectedDataType,
        contributionsShown: next.contributionsShown,
        waterfallShown: next.waterfallShown,
      },
    }
    props.onCardPrefsChange?.(updated)
    putUserPreference(cardPrefsContextKey(props.cardPrefs.dashboardName), updated)
  }

  const dataLoading = useIsAnalysisLoading();
  const factorRiskData = useFactorLens();

  const factorData = React.useMemo<FactorLensDataSet | null>(() => {
    if (!factorRiskData) return null;
    if (cardState.selectedDataType === 'Benchmark') return factorRiskData.benchmark;
    if (cardState.selectedDataType === 'Active') return factorRiskData.active;
    return factorRiskData.portfolio;
  }, [factorRiskData, cardState.selectedDataType]);

  const selectDataType = (dataType: string) => {
    setCardState(prev => ({ ...prev, selectedDataType: dataType }))
    saveCardPrefs({
      selectedDataType: dataType,
      contributionsShown: cardState.contributionsShown,
      waterfallShown: cardState.waterfallShown,
    })
  }

  const selectPortfolio = () => selectDataType('Portfolio')
  const selectBenchmark = () => selectDataType('Benchmark')
  const selectActive = () => selectDataType('Active')

  const selectFactorBox = (focus: FactorLensFocus) => {
    // Drill-down focus is transient; do not persist.
    setCardState(prev => ({ ...prev, selectedFactorBox: focus }))
  };

  const toggleContributionsVisibility = () => {
    const next = !cardState.contributionsShown
    setCardState(prev => ({ ...prev, contributionsShown: next }))
    saveCardPrefs({
      selectedDataType: cardState.selectedDataType ?? 'Portfolio',
      contributionsShown: next,
      waterfallShown: cardState.waterfallShown,
    })
  }

  const toggleWaterfallVisibility = () => {
    const next = !cardState.waterfallShown
    setCardState(prev => ({ ...prev, waterfallShown: next }))
    saveCardPrefs({
      selectedDataType: cardState.selectedDataType ?? 'Portfolio',
      contributionsShown: cardState.contributionsShown,
      waterfallShown: next,
    })
  }

  const cardLayout = {
    width: '100%',
    height: '100%',
  };

  const getButtonStyle = (buttonTitle = '') => {
    let stateStyle = {
      ...buttonGroupDeselectedStyle,
      color: theme.textPrimary,
    };

    if (buttonTitle === cardState.selectedDataType) {
      stateStyle = {
        ...buttonGroupSelectedStyle,
        backgroundColor: theme.tabSelectedBackground,
        color: theme.textPrimary,
      };
    }

    return {...{flex: 1}, ...stateStyle};
  };

  const tableData = (focus: FactorLensFocus | null) => {
    if(factorData === null
      || factorData.factors === null)
    {
      return [];
    }

    // Collapse all Specific Risk records into one synthetic row — the API returns
    const specificRiskRecords = factorData.factors.filter(f => f.level1 === 'Specific Risk');
    const nonSpecificRiskRecords = factorData.factors.filter(f => f.level1 !== 'Specific Risk');
    const specificRiskRow = specificRiskRecords.length > 0
      ? [{
          ...specificRiskRecords[0],
          level2: 'Specific Risk',
          level3: 'Specific Risk',
          level4: 'Specific Risk',
          level5: 'Specific Risk',
          factor_name: 'Specific Risk',
          risk_contrib: specificRiskRecords.reduce((sum, f) => sum + (f.risk_contrib ?? 0), 0),
          weighted_exposure: null as unknown as number,
          factor_risk: null as unknown as number,
        }]
      : [];

    if(focus === null ||
      focus.level === 0 ||
      focus.name === 'Total Risk')
    {
      return [...nonSpecificRiskRecords, ...specificRiskRow];
    }

    //Filter at the specified level
    if(focus.level == 1)
    {
      if (focus.name === 'Specific Risk') return specificRiskRow;
      return nonSpecificRiskRecords.filter(
        factors => factors.level1 == focus.name);
    }

    if(focus.level == 2)
    {
      return factorData.factors.filter(
        factors => factors.level2 == focus.name);
    }

    if(focus.level == 3)
    {
      return factorData.factors.filter(
        factors => factors.level3 == focus.name);
    }

    if(focus.level == 4)
    {
      return factorData.factors.filter(
        factors => factors.level4 == focus.name);
    }

    if(focus.level == 5)
    {
      return factorData.factors.filter(
        factors => factors.level5 == focus.name);
    }
    
    return [];
  }

  const cardUninit = () => {
    return (
      <></>
    );
  }

  const cardLoading = () => {
    //TODO: Use one shared "card loading" component

    return (
      <div style={{margin: 5}}>
        <CircularProgress size={'3rem'} aria-label="Loading…" />
      </div>
    );
  }

  const cardContent = () => {
    return(
      <div style={{flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column'}}>
        {/* Header */}
        <div style={{width: '100%', height: '5%', alignContent: 'center', justifyItems: 'center'}}>
            <div style={{display: 'flex', width: '100%', borderRadius: '8px', backgroundColor: theme.tabBackground}}>
              <Button
                style={getButtonStyle('Portfolio')}
                onClick={() => {
                  selectPortfolio();
                }}
              >
                Portfolio Risk
              </Button>
              <Button
                style={getButtonStyle('Benchmark')}
                onClick={() => {
                  selectBenchmark();
                }}
              >
                Benchmark Risk
              </Button>
              <Button
                style={getButtonStyle('Active')}
                onClick={() => {
                  selectActive();
                }}
              >
                Active Risk
              </Button>
            </div>
        </div>

        {/* FactorGrid and Top Contributions */}
        <div style={{display: 'flex', width: '100%', height: '45%', overflow: 'hidden', minWidth: 0}}>
            <div style={{
                flex: 6,
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                minWidth: 0,
              }}>
                <div style={{
                  flex: 2,
                  minHeight: 0,
                }}>
                  <FactorLensGrid
                    gridData={factorData!}
                    cardState={cardState}
                    setSelected={selectFactorBox}
                    colors={factorRiskColors}
                  ></FactorLensGrid>
                </div>
                <FactorWaterfall
                  key={cardState.selectedDataType}
                  shown={cardState.waterfallShown}
                  toggleVisibility={toggleWaterfallVisibility}
                  factorData={factorData}
                  colors={factorRiskColors} />
            </div>
            {cardState.selectedDataType === 'Portfolio' &&
              <FactorContributions
                shown={cardState.contributionsShown}
                toggleVisibility={toggleContributionsVisibility}
                cardPrefs={props.cardPrefs}
                onCardPrefsChange={props.onCardPrefsChange}/>
            }
        </div>

        {/* Factor Breakdown */}
        <div style={{width: '100%', height: '50%', border: `1px solid ${theme.border}`}}>
            <FactorRiskTable
              factors={tableData(cardState.selectedFactorBox)}
              selectedFactor={cardState.selectedFactorBox}
              loading={false}
              cardPrefs={props.cardPrefs}
              onCardPrefsChange={props.onCardPrefsChange}
            />
        </div>
      </div>
    )
  }

  return (
    <div 
      style={{
        ...cardStyle,
        ...cardLayout,
        backgroundColor: theme.cardBackground,
        border: `1px solid ${theme.border}`,
      }}>
          
      <Stack padding={4} width={'100%'} sx={{ boxSizing: 'border-box', height: '100%' }}>
        <Typography style={{...cardTitleStyle, color: theme.textPrimary}}>
          {CardTitleLookup[props.cardId]}
        </Typography>
        <Typography style={{...cardSubtitleStyle, color: theme.textSecondary}}>
          Breakdown of portfolio, benchmark, and active risk by market factors
        </Typography>

        { !factorRiskData && !dataLoading?
          cardUninit()
          :
          dataLoading ?
            cardLoading()
          :
            cardContent()
        }
      </Stack>
    </div>
  );
};

export default FactorRiskCard;